// src/pages/locations/LocationSEOPage.jsx
// Single reusable component for ALL 35 SEO pages.
// React Router passes :locSlug and :pageType from the URL.

import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { SEO_LOCATIONS, SEO_PAGE_TYPES, TESTIMONIALS } from "../../data/seoLocations";

const SITE_URL = "https://www.seekersbay.in";

/* ─── tiny SEO head helper (no extra library needed) ─── */
function useSEOMeta({ title, description, keywords, canonical, enabled = true }) {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    document.title = title;
    const setMeta = (name, content, prop = false) => {
      const attr  = prop ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description",       description);
    setMeta("keywords",          keywords);
    setMeta("robots",            "index, follow");
    setMeta("og:title",          title,       true);
    setMeta("og:description",    description, true);
    setMeta("og:type",           "website",   true);
    setMeta("og:url",            canonical,   true);
    setMeta("twitter:card",      "summary_large_image");
    setMeta("twitter:title",     title);
    setMeta("twitter:description", description);

    // canonical
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = canonical;

    return () => { document.title = "SeekersBay"; };
  }, [title, description, keywords, canonical, enabled]);
}

/* ─── inline JSON-LD schema injector ─── */
function SchemaScript({ schema }) {
  useEffect(() => {
    const script = document.createElement("script");
    script.type  = "application/ld+json";
    script.text  = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, [schema]);
  return null;
}

/* ─── small reusable bits ─── */
const StarRating = ({ count = 5 }) => (
  <span className="text-[#FF6B35]">{"★".repeat(count)}</span>
);

const CheckItem = ({ text }) => (
  <li className="flex items-start gap-3 text-sm text-gray-600 leading-relaxed">
    <span className="w-5 h-5 rounded-full bg-[#FF6B35] flex items-center justify-center flex-shrink-0 mt-0.5">
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
    {text}
  </li>
);

const PillTag = ({ label }) => (
  <span className="inline-flex items-center gap-1 border border-gray-600 rounded-full px-3 py-1 text-xs font-semibold text-gray-300">
    <span className="text-[#FF6B35] text-sm font-black">✦</span>
    {label}
  </span>
);

const SectionLabel = ({ children, dark = false }) => (
  <div className={`inline-flex items-center gap-2 border rounded-full px-4 py-1 text-sm font-bold mb-4
    ${dark
      ? "border-gray-600 bg-[#1a2a3a] text-gray-300"
      : "border-gray-200 bg-white text-gray-700"}`}>
    <span className="text-[#FF6B35] font-black text-base">✦</span>
    {children}
  </div>
);

const GoogleLogo = () => (
  <svg viewBox="0 0 48 48" className="w-8 h-8 flex-shrink-0">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

/* ══════════════════════════════════════════════════════════
   MAIN PAGE COMPONENT
   ══════════════════════════════════════════════════════════ */
export default function LocationSEOPage() {
  const { pathname } = useLocation();

  const getSeoParams = () => {
    const path = pathname.replace(/^\/+|\/+$/g, "");

    if (!path) {
      return null;
    }

    if (path.startsWith("locations/")) {
      const parts = path.split("/");
      if (parts.length === 3) {
        const [, pageTypeSlug, locSlug] = parts;
        return { pageTypeSlug, locSlug };
      }
      return null;
    }

    const markerIndex = path.lastIndexOf("-in-");
    if (markerIndex === -1) {
      return null;
    }

    return {
      pageTypeSlug: path.slice(0, markerIndex),
      locSlug: path.slice(markerIndex + 4),
    };
  };

  const seoParams = getSeoParams();
  const loc = seoParams
    ? SEO_LOCATIONS.find((item) => item.slug === seoParams.locSlug)
    : null;
  const pageType = seoParams
    ? SEO_PAGE_TYPES.find((item) => item.type === seoParams.pageTypeSlug)
    : null;
  const isValidSeoPage = Boolean(loc && pageType);
  const features = isValidSeoPage
    ? (typeof pageType.features === "function" ? pageType.features(loc) : pageType.features)
    : [];

  // look up data
  const canonical = isValidSeoPage
    ? `${SITE_URL}/${seoParams.pageTypeSlug}-in-${seoParams.locSlug}`
    : `${SITE_URL}${pathname}`;

  // SEO meta
  useSEOMeta({
    title:       isValidSeoPage ? pageType.title(loc) : "Page not found | SeekersBay",
    description: isValidSeoPage ? pageType.metaDesc(loc) : "The requested page could not be found.",
    keywords:    isValidSeoPage ? pageType.keywords(loc) : "",
    canonical,
    enabled: isValidSeoPage,
  });

  if (!isValidSeoPage) {
    return (
      <section className="min-h-[65vh] flex items-center justify-center bg-[#F8F5EE] px-6 py-20">
        <div className="max-w-xl w-full text-center bg-white rounded-3xl shadow-sm border border-[#EDE9E2] p-10">
          <p className="text-[#FF6B35] text-sm font-black uppercase tracking-[0.2em] mb-3">
            SEO Page Not Found
          </p>
          <h1 className="text-3xl font-black text-[#0D1B2A] mb-4">
            We could not find that location page
          </h1>
          <p className="text-gray-500 leading-relaxed mb-8">
            Try one of the homepage location links or return to the main site.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-full bg-[#FF6B35] px-6 py-3 text-sm font-bold text-white transition-all hover:bg-[#e55a25]"
            >
              Back to Home
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-full border-2 border-[#FF6B35] px-6 py-3 text-sm font-bold text-[#FF6B35] transition-all hover:bg-[#FF6B35] hover:text-white"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    );
  }

  // Schemas
  const businessSchema = {
    "@context": "https://schema.org",
    "@type": pageType.schemaType,
    name: `SeekersBay — ${pageType.h1(loc)}`,
    description: pageType.metaDesc(loc),
    url: canonical,
    telephone: "+91-7618759333",
    address: {
      "@type": "PostalAddress",
      addressLocality: loc.name,
      addressRegion: "Karnataka",
      addressCountry: "IN",
    },
    areaServed: [loc.name, ...loc.nearby.split(", ")],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "250",
      bestRating: "5",
    },
    openingHours: "Mo-Sa 07:30-18:00",
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: pageType.faq(loc).map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const localSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "SeekersBay Preschool & Daycare",
    telephone: "+91-7618759333",
    address: {
      "@type": "PostalAddress",
      addressLocality: `${loc.name}, Bangalore`,
      addressRegion: "Karnataka",
      postalCode: "560100",
      addressCountry: "IN",
    },
    url: SITE_URL,
    priceRange: "$$",
  };

  return (
    <>
      {/* ── JSON-LD ── */}
      <SchemaScript schema={businessSchema} />
      <SchemaScript schema={faqSchema} />
      <SchemaScript schema={localSchema} />

      {/* ── BREADCRUMB ── */}
      <div className="bg-[#F8F5EE] py-2.5 border-b border-[#EDE9E2]">
        <div className="max-w-6xl mx-auto px-6 text-xs text-gray-400 font-semibold flex items-center gap-1.5">
          <Link to="/" className="text-[#FF6B35] hover:underline">Home</Link>
          <span>›</span>
          <Link to="/locations" className="text-[#FF6B35] hover:underline">Locations</Link>
          <span>›</span>
          <span className="text-gray-600">{pageType.h1(loc)}</span>
        </div>
      </div>

      {/* ══ HERO ══ */}
      <section className="relative min-h-[88vh] flex flex-col justify-end pb-14 overflow-hidden bg-gradient-to-br from-[#0D1B2A] via-[#1B4D6E] to-[#FF6B35]">
        {/* overlays */}
        <div className="absolute inset-0 bg-[#0D1B2A]/55" />
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#FF6B35]/10 -translate-y-1/3 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-[#1B4D6E]/25 translate-y-1/3 -translate-x-1/3" />

        <div className="relative z-10 max-w-6xl mx-auto px-6 lg:px-16 w-full">
          {/* badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-xs font-bold px-4 py-2 rounded-full mb-7">
            <span className="w-2 h-2 rounded-full bg-[#FF6B35] animate-pulse" />
            Play, Learn, And Shine — {loc.name}
          </div>

          {/* H1 */}
          <div className="mb-5 max-w-3xl">
            <h2 className="text-white font-extrabold mb-1 opacity-90"
              style={{ fontSize: "clamp(1.3rem,3vw,1.9rem)" }}>
              Small Joy, Big Leaps:
            </h2>
            <h1 className="text-white font-black leading-tight"
              style={{ fontSize: "clamp(2rem,5.5vw,4rem)" }}>
              {pageType.heroTag(loc)}
            </h1>
          </div>

          <p className="text-white font-semibold leading-relaxed mb-9 max-w-xl"
            style={{ fontSize: "clamp(0.92rem,1.4vw,1.05rem)" }}>
            {pageType.heroSub(loc)}
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap items-center gap-4 mb-10">
            <Link to="/contact"
              className="inline-flex items-center gap-2.5 bg-[#FF6B35] hover:bg-[#e55a25] text-white font-bold px-7 py-3.5 rounded-full text-sm transition-all hover:shadow-xl hover:shadow-orange-500/35 hover:-translate-y-0.5">
              Book A School Visit
              <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">→</span>
            </Link>
            <a href="tel:+917618759333"
              className="inline-flex items-center gap-2 border border-white/40 text-white font-bold px-7 py-3.5 rounded-full text-sm hover:bg-white/10 transition-all">
              📞 +91-7618759333
            </a>
          </div>

          {/* stats */}
          <div className="flex flex-wrap items-center gap-8">
            {[
              { val: "4.9★",  label: "Google Rating" },
              { val: "250+",  label: "Happy Families" },
              { val: "Top 10",label: "Bangalore Preschool" },
            ].map((s, i) => (
              <div key={i} className="flex items-center gap-8">
                {i > 0 && <div className="w-px h-9 bg-white/20" />}
                <div className="text-center">
                  <div className="text-2xl font-black text-[#FF6B35]">{s.val}</div>
                  <div className="text-[10px] text-white/55 font-bold uppercase tracking-widest">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ ABOUT / WHY US ══ */}
      <section className="bg-white py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* visual */}
            <div className="relative">
              <div className="bg-gradient-to-br from-[#0D1B2A] to-[#1B4D6E] rounded-2xl p-12 text-center min-h-[300px] flex flex-col items-center justify-center">
                <div className="text-7xl mb-4">🏫</div>
                <div className="text-white text-xl font-black mb-2">SeekersBay</div>
                <div className="text-white/70 text-sm font-semibold mb-3">Ranked Bangalore's Top 10</div>
                <div className="flex gap-1 text-[#FF6B35]">★★★★★</div>
                <div className="text-white/80 text-sm font-bold mt-1">4.9 / 5 · 250+ Reviews</div>
              </div>
              {/* floating badge */}
              <div className="absolute -bottom-5 -right-5 bg-[#FF6B35] rounded-2xl p-4 shadow-xl shadow-orange-500/30">
                <div className="text-white/80 text-[10px] font-bold uppercase tracking-wider">Serving</div>
                <div className="text-white text-base font-black">{loc.name}</div>
                <div className="text-white/75 text-xs">&amp; {loc.nearby}</div>
              </div>
            </div>

            {/* content */}
            <div>
              <SectionLabel>About SeekersBay in {loc.name}</SectionLabel>
              <h2 className="text-3xl md:text-4xl font-black text-[#0D1B2A] leading-tight mb-4">
                Nurturing young learners in {loc.name} with care &amp; creativity
              </h2>
              <p className="text-gray-500 text-[15px] leading-relaxed mb-6">
                We provide an inspiring space where children in {loc.name} discover new skills, build confidence, and enjoy learning through hands-on activities.
                Families from {loc.nearby} also trust SeekersBay for exceptional early education.
              </p>
              <ul className="flex flex-col gap-2.5 mb-8">
                {features.map((f, i) => <CheckItem key={i} text={f} />)}
              </ul>
              <div className="flex flex-wrap gap-3">
                <Link to="/about"
                  className="inline-flex items-center gap-2 bg-[#FF6B35] hover:bg-[#e55a25] text-white font-bold px-6 py-3 rounded-full text-sm transition-all">
                  Know More About Us <span>→</span>
                </Link>
                <Link to="/contact"
                  className="inline-flex items-center gap-2 border-2 border-[#FF6B35] text-[#FF6B35] hover:bg-[#FF6B35] hover:text-white font-bold px-6 py-3 rounded-full text-sm transition-all">
                  Get In Touch
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ PROGRAMS ══ */}
      <section className="bg-[#F8F5EE] py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-16">
          <div className="text-center mb-12">
            <SectionLabel>Our Programs</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-black text-[#0D1B2A]">
              Programs for Every Stage of Learning
            </h2>
            <p className="text-gray-500 text-[15px] mt-3 max-w-md mx-auto leading-relaxed">
              Tailored programmes for children aged 1.5–6 years, designed to nurture every child's unique potential in {loc.name}.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {pageType.programs.map((prog, i) => (
              <div key={i}
                className="flex flex-col bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="flex-1 p-5">
                  <div className="w-12 h-12 rounded-full bg-[#1B4D6E] flex items-center justify-center text-2xl mb-4">
                    {prog.icon}
                  </div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <h3 className="text-base font-extrabold text-[#0D1B2A]">{prog.title}</h3>
                    <span className="bg-[#FFF0EA] text-[#FF6B35] text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
                      Age {prog.age}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed">{prog.desc}</p>
                </div>
                {/* colourful footer strip */}
                <div className="h-2 bg-gradient-to-r from-[#1B4D6E] to-[#FF6B35]" />
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/programs"
              className="inline-flex items-center gap-2 bg-[#FF6B35] hover:bg-[#e55a25] text-white font-bold px-8 py-3.5 rounded-full transition-all hover:-translate-y-0.5">
              View All Programs →
            </Link>
          </div>
        </div>
      </section>

      {/* ══ WHY CHOOSE US (dark) ══ */}
      <section className="bg-[#0D1B2A] py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-16">
          <div className="text-center mb-12">
            <SectionLabel dark>Why Choose SeekersBay in {loc.name}</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-black text-white">
              Why parents in {loc.name} choose us
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-8">
            {[
              { icon: "👩‍🏫", title: "Experienced & Caring Teachers",    body: `2 certified teachers per 12 children ensure every child in ${loc.name} receives personal attention, guidance, and encouragement.`,  tags: ["Learning", "Growth"] },
              { icon: "🥗", title: "Healthy Meals & Snacks",            body: "Nutritious, home-style meals are served throughout the day, planned by nutrition-conscious educators to fuel growing minds.",      tags: ["Nutrition", "Wellness"] },
              { icon: "📱", title: "Strong Parent Communication",        body: `Daily updates, regular parent-teacher meetings, and an open-door policy keep ${loc.name} families always in the loop.`,           tags: ["Updates", "Trust"] },
              { icon: "🔒", title: "Safe, CCTV-Monitored Campus",       body: `Our child-proofed premises are fully CCTV-monitored with controlled access, giving ${loc.name} parents complete peace of mind.`,   tags: ["Safety", "Security"] },
            ].map((card, i) => (
              <div key={i} className="bg-[#1a2a3a] rounded-2xl p-6">
                <div className="text-4xl mb-4">{card.icon}</div>
                <h3 className="text-white font-extrabold text-base mb-2">{card.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">{card.body}</p>
                <div className="flex flex-wrap gap-2">
                  {card.tags.map((t) => <PillTag key={t} label={t} />)}
                </div>
              </div>
            ))}
          </div>

          {/* inline CTA */}
          <div className="bg-[#1a2a3a] rounded-2xl p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <div>
              <p className="text-gray-400 text-sm font-bold mb-1">Ready to give your child the best start?</p>
              <p className="text-white text-xl font-black">Enrol at SeekersBay, {loc.name} Today</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="tel:+917618759333"
                className="inline-flex items-center gap-2 bg-[#FF6B35] hover:bg-[#e55a25] text-white font-bold px-6 py-3 rounded-full text-sm transition-all">
                📞 Call Us Now
              </a>
              <Link to="/contact"
                className="inline-flex items-center gap-2 border border-white/30 text-white font-bold px-6 py-3 rounded-full text-sm hover:bg-white/10 transition-all">
                Book A Visit
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ══ TESTIMONIALS ══ */}
      <section className="bg-[#F5F0E8] py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-6 lg:px-16">
          <div className="text-center mb-12">
            <SectionLabel>Testimonials</SectionLabel>
            <h2 className="text-3xl md:text-4xl font-black text-[#0D1B2A]">
              Hear what happy parents are saying
            </h2>
            {/* google pill */}
            <div className="inline-flex items-center gap-3 mt-5 bg-white rounded-full px-5 py-2.5 shadow-sm">
              <GoogleLogo />
              <div className="text-left">
                <div className="flex items-center gap-2">
                  <span className="text-xl font-black text-gray-900">4.9/5</span>
                  <StarRating />
                </div>
                <p className="text-xs text-gray-400 font-semibold">Based on 250 reviews</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="mb-3"><StarRating count={t.stars} /></div>
                <p className="text-gray-700 text-sm leading-relaxed mb-5">"{t.quote}"</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-extrabold text-[#0D1B2A] text-sm">{t.name}</p>
                    <p className="text-xs text-gray-400">{t.role}</p>
                  </div>
                  <span className="text-5xl text-[#FF6B35] leading-none font-serif">"</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FAQ ══ */}
      <section className="bg-white py-16 lg:py-20" itemScope itemType="https://schema.org/FAQPage">
        <div className="max-w-3xl mx-auto px-6">
          <SectionLabel>FAQ</SectionLabel>
          <h2 className="text-3xl md:text-4xl font-black text-[#0D1B2A] mb-10">
            Frequently Asked Questions
          </h2>
          <div className="divide-y divide-gray-100">
            {pageType.faq(loc).map((item, i) => (
              <div key={i} className="py-5"
                itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
                <h3 itemProp="name" className="text-base font-extrabold text-[#0D1B2A] mb-2">
                  {item.q}
                </h3>
                <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                  <p itemProp="text" className="text-sm text-gray-500 leading-relaxed">{item.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FINAL CTA ══ */}
      <section className="bg-gradient-to-r from-[#FF6B35] to-[#e55a25] py-16 lg:py-20 text-center">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
            {pageType.ctaSection(loc)}
          </h2>
          <p className="text-white/85 text-[15px] leading-relaxed mb-9 font-semibold">
            {pageType.ctaSub(loc)}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact"
              className="inline-flex items-center gap-2 bg-white text-[#FF6B35] font-black px-8 py-4 rounded-full text-[15px] hover:shadow-xl transition-all hover:-translate-y-0.5">
              Book A Free School Visit →
            </Link>
            <a href="tel:+917618759333"
              className="inline-flex items-center gap-2 bg-white/20 border-2 border-white/50 text-white font-bold px-8 py-4 rounded-full text-[15px] hover:bg-white/30 transition-all">
              📞 +91-7618759333
            </a>
          </div>
          <p className="text-white/65 text-xs mt-6 font-semibold">
            Serving {loc.name}, {loc.nearby}, and all of South Bangalore
          </p>
        </div>
      </section>
    </>
  );
}
